import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AiFillLike, AiFillDislike } from "react-icons/ai";

// Components
import Button from '../../../../components/button';
import Streak from '../../../../components/Streak';
import GradedDate from '../../../../components/GradedDate';

// Utils
import * as urls from '../../../../utils/urls';

// Styles
import styles from './finished.module.css';
import colors from '../../../../utils/styles/colors.module.css';

// Assets
import mascot1 from '../../../../components/assets/logo.png';
import greenCheck from '../../../../components/assets/greenCheck.png';

// Types
import { KeyDate } from '../../../../types';


interface FinishedProps {
  localQuestions: any[];
  startTime: number;
  user: any;
  keyDate: KeyDate | null;
  onBack: () => void;
  onComplete: () => void;
  type: 'quiz' | 'practice';
  title: string;
  quizId?: string;
  sessionId?: string; // Add sessionId prop
}

const Finished: React.FC<FinishedProps> = ({ localQuestions, startTime, user, keyDate, onBack, onComplete, type, title, quizId, sessionId }) => {
  const { t } = useTranslation();

  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [completionRecorded, setCompletionRecorded] = useState(false);

  // Effects
  useEffect(() => {
    const totalQuestions = localQuestions.length;
    let correct = 0;

    localQuestions.forEach(question => {
      if (question.is_user_answer_correct) {
        correct++;
      }
    });

    setCorrectAnswers(correct);
    setTotalQuestions(totalQuestions);
  }, [localQuestions]);

  // Record quiz completion when component mounts
  useEffect(() => {
    if (quizId && sessionId && startTime > 0 && !completionRecorded) {
      recordQuizCompletion();
    }
  }, [quizId, sessionId, startTime, completionRecorded]);

  // Handlers
  const recordQuizCompletion = async () => {
    if (!quizId || !sessionId) return;

    try {
      const finishedTime = Date.now();
      const elapsedMs = finishedTime - startTime;
      // Send duration in milliseconds, not seconds
      const duration = elapsedMs;

      const options = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        body: JSON.stringify({
          duration: duration,
          session_id: sessionId
        }),
      };

      const response = await fetch(urls.quizCompleteUrl(quizId), options);

      if (response.ok) {
        setCompletionRecorded(true);
      } else {
        console.error('Failed to record quiz completion');
      }
    } catch (error) {
      console.error('Error recording quiz completion:', error);
    }
  };

  const handleRate = (liked: boolean) => {
  }

  const handleComplete = () => {
    onComplete();
  }

  const handleBackToSmartNote = () => {
    onBack();
  }

  // Render
  const renderAccuracy = () => {
    const accuracy = (correctAnswers / totalQuestions) * 100;
    let color = '';
    if (accuracy === 100) color = colors.greenText;
    if (accuracy < 100) color = colors.yellowText;
    if (accuracy < 80) color = colors.amberText;
    if (accuracy < 60) color = colors.redText;

    return (
      <p className={`text-2xl ${color}`}>
        {accuracy.toFixed(0)}%
      </p>
    )
  }

  const renderElapsedTime = () => {
    const finishedTime = Date.now();
    const elapsedMs = finishedTime - startTime;
    const elapsedSeconds = Math.floor(elapsedMs / 1000);
    const m = Math.floor(elapsedSeconds / 60);
    const s = elapsedSeconds % 60;

    return (
      <p className='text-2xl text-blue-500'>
        {m}:{s.toString().padStart(2, '0')}
      </p>
    )
  }

  const renderStreak = () => {
    return <Streak streak={10} type='quiz' />
  }

  const renderCorrectAnswers = () => {
    return (
      <p className='text-2xl text-green-500'>
        {correctAnswers}
        /
        {totalQuestions}
      </p>
    )
  }

  return (
    <div className={styles.container}>
      <img src={mascot1} alt='mascot' className={styles.mascot} />

      <div className={styles.content}>
        <h1 className='text-2xl text-black dark:text-white'>
          {t('questionCards.finished.nice_job', { name: user.full_name })}
        </h1>
        <p className='text-md text-gray-500 text-center'>
          {t('questionCards.finished.test_completed.part_1')}

          <span className='text-black dark:text-white'>
            {title}
          </span>

          {t('questionCards.finished.test_completed.part_2')}
        </p>
      </div>

      {type === 'practice' && (
        renderStreak()
      )}

      <div className={styles.stats}>
        {/* Accuracy */}
        <div className={styles.stat}>
          <div className='flex flex-row items-center gap-2'>
            <span className='text-2xl'>🎯</span>

            {renderAccuracy()}
          </div>

          <p className='text-md text-gray-500 dark:text-gray-400'>
            {t('questionCards.finished.accuracy')}
          </p>
        </div>

        {/* Correct Answers */}
        {type === 'practice' && (
          <div className={styles.stat}>
            <div className='flex flex-row items-center gap-2'>
              <img src={greenCheck} alt='green check' className='w-6 h-6' />

              {renderCorrectAnswers()}
            </div>

            <p className='text-md text-gray-500 dark:text-gray-400'>
              {t('questionCards.finished.correct')}
            </p>
          </div>
        )}

        {/* Elapsed Time */}
        <div className={styles.stat}>
          <div className='flex flex-row items-center gap-2'>
            <span className='text-2xl'>⏰</span>

            {renderElapsedTime()}
          </div>

          <p className='text-md text-gray-500 dark:text-gray-400'>
            {t('questionCards.finished.time')}
          </p>
        </div>
      </div>

      <div className={styles.rateContainer}>
        <p className='text-md text-gray-500 dark:text-gray-400'>
          {t('questionCards.finished.how_did_the_quiz_go')}
        </p>

        {/* <div className={styles.rateButtons}>
          <button className={styles.rateButton} onClick={() => handleRate(true)}>
            <AiFillLike className={colors.grayText} />
          </button>

          <button className={styles.rateButton} onClick={() => handleRate(false)}>
            <AiFillDislike className={colors.grayText} />
          </button>
        </div> */}
      </div>

      {type === 'practice' && keyDate && <GradedDate keyDate={keyDate} />}

      <div className={styles.footer}>
        <Button type='primary' onClick={handleComplete} className={`${styles.actionButton} dark:bg-stone-650 dark:hover:bg-zeus-900`}>
          {t('actions.complete')}
        </Button>

        {/* # TODO: Remove this one if not needed anymore */}
        {/* <Button type='secondary' onClick={handleBackToSmartNote}>
          {t('questionCards.finished.back_to_smart_note')}
        </Button> */}
      </div>
    </div>
  )
}

export default Finished;
